/**
 * @template {{ key: string; }} T
 * @template {HTMLElement} U
 */
class NodeHash {
  domNode = document.createElement('div');

  /**
   * @param {object} config
   * @param {string} [config.className]
   * @param {(p: T) => HTMLElement} config.createNode
   * @param {string} [config.id]
   * @param {Array<T>} [config.initItems]
   * @param {boolean} [config.noNestedDiv]
   */
  constructor (config) {
    const {
      className = '',
      createNode,
      id = null,
      initItems = null,
      noNestedDiv = false
    } = config;

    if (noNestedDiv) {
      /** @private */
      this.containerNode = this.domNode;
    } else {
      /** @private */
      this.containerNode = document.createElement('div');
      this.domNode.appendChild(this.containerNode);
    }

    /** @private */
    this.id = id;

    if (this.id) {
      this.domNode.id = this.id;
    }

    if (className) {
      this.domNode.className = className;
    }

    /** @private @type {Record<string, HTMLElement>} */
    this.nodeHash = {};

    /** @private */
    this.createNode = createNode;

    /** @private */
    this.dataAttr = 'data-nodehash' + NodeHash.count + '-key';

    NodeHash.count++;

    if (initItems) {
      this.add(initItems);
    }
  }

  destroy () {
    this.domNode.remove();
  }

  /**
   * @param {Array<T>} itemParams
   */
  add (itemParams) {
    _makeArray(itemParams).forEach(param => {
      this.containerNode.appendChild(this._initNode(param));
    });
  }

  /**
   * @param {Array<string>} keys
   */
  remove (keys) {
    _makeArray(keys).forEach(key => {
      if (this.containsKey(key)) {
        this.nodeHash[key].remove();
        delete this.nodeHash[key];
      }
    });
  }

  /**
   * @param {string} key
   */
  containsKey (key) {
    return !!this.nodeHash[key];
  }

  /**
   * @param {string} key
   */
  get (key) {
    return this.containsKey(key) ? this.nodeHash[key] : null;
  }

  /**
   * @deprecated
   * @private
   */
  getIndex (key) {
    let i = 0;
    let node = this.nodeHash[key];

    if (!node) {
      return -1;
    }

    while ((node = node.previousSibling) !== null) {
      i++;
    }

    return i;
  }

  /**
   * @param {U} node
   */
  getKey (node) {
    if (!node) {
      return null;
    }

    return node.getAttribute(this.dataAttr);
  }

  /**
   * @param {U} node
   */
  isItem (node) {
    const key = this.getKey(node);

    return !!key && !!this.containsKey(key);
  }

  /**
   * @param {string} eventName
   * @param {(node: U, key: string, event: Event) => void} callback
   * @param {any} [ref]
   * @param {number} [pauseInterval]
   */
  itemEventListener (eventName, callback, ref = null, pauseInterval = 0) {
    const thisArg = ref || this;
    const selector = '[' + this.dataAttr + ']';
    const pauseOnEvent = pauseInterval > 0;

    /** @private */
    this.isPaused = false;

    document.body.addEventListener(eventName, event => {
      // @ts-ignore
      if (!event.target.closest(selector) && pauseOnEvent && this.isPaused) {
        return;
      }

      this.isPaused = pauseOnEvent;

      // @ts-ignore
      const key = this._getContainingItemKey(event.target);

      if (!key) {
        return;
      }

      callback.call(thisArg, this.nodeHash[key], key, event);

      if (pauseOnEvent) {
        setTimeout(() => {
          this.isPaused = false;
        }, pauseInterval);
      }
    });
  }

  /**
   * @param {U} node
   */
  _getContainingItemKey (node) {
    return this.getKey(this._getContainingItem(node));
  }

  /**
   * Travels up the document tree to find the hash item element
   * @private
   * @param {U} node
   */
  _getContainingItem (node) {
    let currentNode = node;

    while (
      currentNode &&
      currentNode !== document.body &&
      !this.isItem(currentNode)
    ) {
      // @ts-ignore
      currentNode = currentNode.parentNode;
    }

    return currentNode;
  }

  /**
   * @private
   * @param {T} param
   */
  _initNode (param) {
    /** @type {U} */
    // @ts-ignore
    const node = this.createNode(param);
    node.setAttribute(this.dataAttr, param.key);
    this.nodeHash[param.key] = node;

    return node;
  }
}

NodeHash.count = 0;

/**
 * @template T
 * @param {Array<T> | T} value
 */
function _makeArray (value) {
  return Array.isArray(value) ? value : [value];
}

export default NodeHash;
